import {
  DeleteOutlined,
  EditOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import {
  Button,
  Col,
  Descriptions,
  Modal,
  Row,
  Spin,
  Tooltip,
  message,
} from "antd";
import { AxiosResponse } from "axios";
import React, { Fragment } from "react";
import { RouteComponentProps } from "react-router";
import { NavLink } from "react-router-dom";
import Card from "../components/Card";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import { Entitlement, Team, User } from "../helpers/shared-interfaces";
import { request } from "../helpers/utilities";
import { DELETE_TEAM, GET_TEAM } from "../gql/teams";

interface State {
  name: string;
  isLoading: boolean;
  hasChanged: boolean;
  id: string | number;
  showDialog: boolean;
  showApiDialog: boolean;
  team: Team | null;
  currentTeamsToProcess: number;
}

interface RouteParams {
  id: string;
}

interface Props
  extends RouteComponentProps<RouteParams, any, { teams?: Team[] }> {
  entitlement: Entitlement;
  user: User;
}

function ModalWrapper(
  showDialog: boolean,
  deleteTeam: () => void,
  toggleDeleteConfirmationModal: () => void,
): JSX.Element {
  return (
    <Modal
      title="Confirm Deletion Action"
      visible={showDialog}
      {...{
        okText: "Yes",
        okType: "danger",
        cancelButtonProps: {
          className: "modal-cancel-btn",
        },
        okButtonProps: {
          className: "modal-confirm-btn",
        },
      }}
      onOk={deleteTeam}
      onCancel={toggleDeleteConfirmationModal}
    >
      <div>Are you sure you want to delete this team?</div>
    </Modal>
  );
}

export default class ViewTeam extends React.Component<Props, State> {
  constructor(props: Readonly<Props>) {
    super(props);
    this.state = {
      isLoading: true,
      id: "",
      name: "",
      hasChanged: false,
      showDialog: false,
      showApiDialog: false,
      team: null,
      currentTeamsToProcess: 0,
    };
  }

  componentDidMount(): void {
    this.setState(
      {
        id: this.props.match.params.id,
        currentTeamsToProcess:
          this.props?.location?.state?.teams?.findIndex(
            (team) =>
              team?.id.toString() === this.props.match.params.id.toString(),
          ) ?? 0,
      },
      (): void => {
        this.getTeamData();
      },
    );
  }

  toggleDeleteConfirmationModal = (): void => {
    this.setState((prevState) => {
      return {
        showDialog: !prevState.showDialog,
      };
    });
  };

  redirectToAllTeamsPage = (): void => {
    this.props.history.push("/teams");
  };

  getTeamData = (): void => {
    const { id } = this.state;
    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: GET_TEAM,
          variables: { id },
        }).then(({ data }: AxiosResponse<any>): void => {
          this.setState({ isLoading: false });
          if (!data.errors) {
            this.setState({
              team: data.data.team,
            });
          } else {
            message.error("Failed to get team data");
          }
        });
      },
    );
  };

  deleteTeam = (): void => {
    const { id } = this.state;

    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: DELETE_TEAM,
          variables: { id },
        })
          .then(({ data }: AxiosResponse<any>): void => {
            this.setState({ isLoading: false, showDialog: false });
            if (!data.errors && data.data.deleteTeam.result) {
              message.success("Team deleted successfuly");
              this.redirectToAllTeamsPage();
            } else {
              message.error("Failed to delete team");
            }
          })
          .catch((): void => {
            this.setState({ isLoading: false, showDialog: false });
          });
      },
    );
  };

  render(): JSX.Element | null {
    const { isLoading, id, showDialog, team, currentTeamsToProcess } =
      this.state;

    const moveToPreviousLink = `/teams/${
      this.props.location?.state?.teams?.[currentTeamsToProcess - 1]?.id
    }`;

    const moveToNextLink = `/teams/${
      this.props.location?.state?.teams?.[currentTeamsToProcess + 1]?.id
    }`;

    const nextPreviousUI = (
      <>
        <br />
        <Row
          gutter={10}
          style={{
            float: "right",
            marginTop: "10px",
          }}
        >
          <Col>
            {this.state.currentTeamsToProcess > 0 && (
              <NavLink
                to={{
                  pathname: moveToPreviousLink,
                  state: {
                    teams: this.props?.location?.state?.teams ?? [],
                  },
                }}
              >
                <Tooltip placement="left" title="Previous">
                  <LeftOutlined style={{ color: "#1D3764" }} />
                </Tooltip>
              </NavLink>
            )}
          </Col>

          <Col>
            {this.props?.location?.state?.teams &&
              this.state.currentTeamsToProcess <
                this.props?.location?.state?.teams?.length - 1 && (
                <NavLink
                  to={{
                    pathname: moveToNextLink,
                    state: {
                      teams: this.props?.location?.state?.teams ?? [],
                    },
                  }}
                >
                  <Tooltip placement="right" title="next">
                    <RightOutlined style={{ color: "#1D3764" }} />
                  </Tooltip>
                </NavLink>
              )}
          </Col>

          <Col>
            {this.props?.location?.state?.teams &&
              `${currentTeamsToProcess + 1} / ${
                this.props?.location?.state?.teams?.length
              }`}
          </Col>
        </Row>
      </>
    );

    return (
      <Spin spinning={isLoading}>
        <Card>
          <Fragment>
            <div>
              {ModalWrapper(
                showDialog,
                this.deleteTeam,
                this.toggleDeleteConfirmationModal,
              )}
              <Descriptions
                column={3}
                title={<div>{team?.name}</div>}
                extra={
                  <div>
                    <EntitledSection
                      entitlement={entitlements.Companies.teams.edit}
                    >
                      <NavLink to={`/teams/${id}/edit`}>
                        <Button
                          icon={<EditOutlined />}
                          size="large"
                          style={{
                            backgroundColor: "  rgb(29, 55, 100)",
                            color: "white",
                            marginRight: "10px",
                          }}
                          data-cy="test-team-edit-button"
                        >
                          Edit
                        </Button>
                      </NavLink>
                    </EntitledSection>
                    <EntitledSection
                      entitlement={entitlements.Companies.teams.delete}
                    >
                      <Button
                        icon={<DeleteOutlined />}
                        size="large"
                        onClick={this.toggleDeleteConfirmationModal}
                        data-cy="test-team-delete-button"
                      >
                        Delete
                      </Button>
                    </EntitledSection>
                    {nextPreviousUI}
                  </div>
                }
              >
                <Descriptions.Item label={<b>Name</b>} key="Name">
                  {team?.name}
                </Descriptions.Item>
                <Descriptions.Item label={<b>Department</b>} key="Category">
                  <NavLink to={`/departments/${team?.department.id}`}>
                    {team?.department.name}
                  </NavLink>
                </Descriptions.Item>
                <Descriptions.Item
                  label={<b>Users</b>}
                  key="Users"
                  contentStyle={{ flexDirection: "column" }}
                >
                  {team?.users?.map((user) => {
                    return (
                      <>
                        <NavLink to={`/users/${user?.id}`}>
                          {user?.name}
                        </NavLink>{" "}
                      </>
                    );
                  })}
                </Descriptions.Item>
              </Descriptions>
            </div>
          </Fragment>
        </Card>
      </Spin>
    );
  }
}
